'use client';

import { useBunja } from 'bunja/react';
import dynamic from 'next/dynamic';
import { useMount } from 'react-use';

import { getPositionHistoryListApi } from '@flipster/apis/v2/history';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { css } from '@flipster/styled-system/css';
import { Col } from '@flipster/ui/Table';
import segment from '@flipster/utils/segment';
import { HistoryPositionEvent } from '@flipster/utils/segment/events/history';

import { useAuth } from '../../../shared/auth/state';
import { useStepPagination } from '../../../shared/pagination/use-step-pagination';
import { HistoryTabGroup } from '../../shared/constants';
import HistoryPageLayout from '../../shared/history-page-layout';
import { TableList } from '../../shared/table-list';
import { historyExportState } from '../../state/export';
import DesktopRow from './desktop-row';
import HighestPnl from './highest-pnl';
import PositionCard from './position-card';

const FilterSection = dynamic(() => import('../../shared/filter-section'), { ssr: false });
export const positionHistoryColumns: Col[] = [
  {
    key: 'date',
    title: (
      <p>
        <FormattedMessage id='Common.r250401.Date' />
        <br />
        (<FormattedMessage id='Autogen_common.r251126.s_Open' /> -{' '}
        <FormattedMessage id='Autogen_common.r251126.s_Close' />)
      </p>
    ),
    width: 160,
  },
  {
    key: 'symbol',
    title: <FormattedMessage id='Assets.r250401.Symbol' />,
    width: 170,
  },
  {
    key: 'side',
    title: <FormattedMessage id='Assets.r250401.Side' />,
    width: 70,
  },
  {
    key: 'margin-mode',
    title: (
      <p className={css({ whiteSpace: 'nowrap' })}>
        <FormattedMessage id='Trade.r251008.Margin_mode' /> /
        <br />
        <FormattedMessage id='Assets.r250401.Leverage' />
      </p>
    ),
    width: 100,
  },
  {
    key: 'pnl',
    title: <FormattedMessage id='Common.r251107.Realized_pnl_roi' />,
    align: 'right',
    width: 140,
  },
  {
    key: 'quantity',
    title: <FormattedMessage id='Assets.r250401.Quantity' />,
    align: 'right',
    width: 140,
  },
  {
    key: 'entry-price',
    title: <FormattedMessage id='Assets.r250401.Entry_price' />,
    align: 'right',
    width: 140,
  },
  {
    key: 'closing-price',
    title: <FormattedMessage id='Assets.r250401.Avg_closing_price' />,
    align: 'right',
    width: 140,
  },
];

interface Props {
  isWebview?: boolean;
  type: 'position' | 'airdrop';
  historyTabGroup: Exclude<HistoryTabGroup, 'Transaction'>;
}

const PositionHistory = ({ isWebview, type, historyTabGroup }: Props) => {
  const { isCookieLoggedIn } = useAuth();
  const isAirdrop = type === 'airdrop';
  const { setExportParams } = useBunja(historyExportState);
  const router = useRouter();

  const { isLoading, params, data, hasPrev, hasNext, setSymbolFilter, setRangeFilter, setPagination, refetch } =
    useStepPagination('history', ['getPositionHistoryListApi'], getPositionHistoryListApi, {
      passParams: { isAirdrop },
      enabled: isCookieLoggedIn,
    });

  useMount(() => {
    if (type === 'position' && isWebview) segment.track(HistoryPositionEvent.PositionHistoryPageView);
  });

  const title = isAirdrop ? (
    <FormattedMessage id='Autogen_common.r251126.s_Airdrop_history' />
  ) : (
    <FormattedMessage id='Autogen_common.r251126.s_Position_history' />
  );

  const handleClickExport = () => {
    setExportParams({
      category: 'perpetual',
      subCategory: isAirdrop ? 'airdrop' : 'position',
      additionalOption: {
        symbol: params?.symbol,
      },
    });
    router.push('/history/export');
  };

  return (
    <HistoryPageLayout title={title} isWebview={isWebview} historyTabGroup={historyTabGroup}>
      <div className={css({ p: { base: '0 16px 48px', tablet: 0 } })} data-test-id='position_filter_section'>
        <HighestPnl isAirdrop={isAirdrop} />
        <section>
          <FilterSection
            params={params}
            setSymbolFilter={setSymbolFilter}
            setRangeFilter={setRangeFilter}
            refetch={refetch}
            isWebview={isWebview}
            onClickExport={handleClickExport}
          />
          <div data-test-id='position_list'>
            <TableList
              isLoading={isLoading}
              items={data}
              renderCardItem={(item) => <PositionCard item={item} key={`card-${item.positionId}`} />}
              renderTableItem={(item) => <DesktopRow item={item} key={`row-${item.positionId}`} />}
              columns={[
                {
                  key: 'date',
                  title: (
                    <p>
                      <FormattedMessage id='Common.r250401.Date' />
                      <br />
                      (<FormattedMessage id='Autogen_common.r251126.s_Open' /> -{' '}
                      <FormattedMessage id='Autogen_common.r251126.s_Close' />)
                    </p>
                  ),
                  width: 160,
                },
                {
                  key: 'symbol',
                  title: <FormattedMessage id='Assets.r250401.Symbol' />,
                  width: 170,
                },
                {
                  key: 'side',
                  title: <FormattedMessage id='Assets.r250401.Side' />,
                  width: 70,
                },
                {
                  key: 'margin-mode',
                  title: (
                    <p className={css({ whiteSpace: 'nowrap' })}>
                      <FormattedMessage id='Trade.r251008.Margin_mode' /> /
                      <br />
                      <FormattedMessage id='Assets.r250401.Leverage' />
                    </p>
                  ),
                  width: 100,
                },
                {
                  key: 'pnl',
                  title: <FormattedMessage id='Common.r251107.Realized_pnl_roi' />,
                  align: 'right',
                  width: 140,
                },
                {
                  key: 'quantity',
                  title: <FormattedMessage id='Assets.r250401.Quantity' />,
                  align: 'right',
                  width: 140,
                },
                {
                  key: 'entry-price',
                  title: <FormattedMessage id='Assets.r250401.Entry_price' />,
                  align: 'right',
                  width: 140,
                },
                {
                  key: 'closing-price',
                  title: <FormattedMessage id='Assets.r250401.Avg_closing_price' />,
                  align: 'right',
                  width: 140,
                },
              ]}
              pagination={{
                cursorKey: 'positionId',
                hasPrev,
                hasNext,
                onChange: ({ before, after }: { before?: string | undefined; after?: string | undefined }) => {
                  window.scrollTo(0, 0);
                  setPagination({ before, after });
                },
              }}
            />
          </div>
        </section>
      </div>
    </HistoryPageLayout>
  );
};

export default PositionHistory;
